import { Stack } from '@mui/material';
import cn from 'classnames';
import { useMemo } from 'react';
import { IChatSession } from 'src/@types/chat';
import Iconify from 'src/components/iconify';
import { useSelector } from 'src/redux/store';
import { FormatTime } from 'src/utils/formatTime';
import { useGatheringChatState } from '../../../GatheringSessions/gatheringChatState';
import {
  ActionsBarButtonStyled,
  StyledListContainer,
  StyledTopLeftIcon,
} from '../../shared/styles';
import { Action } from '../../shared/types';
import { useActionBarState } from '../../useActionBarState';

type Props = {
  showBorder?: boolean;
  toggleOpenAction: (action: Action, fromClose?: boolean) => void;
};

export const OpenedGatheringChats = ({
  showBorder,
  toggleOpenAction,
}: Props) => {
  const { sessionId, chatSessionsList, closeChatSession, setSessionId } =
    useGatheringChatState();
  const { openAction } = useActionBarState();

  const chatList = useMemo(
    () => Object.values(chatSessionsList),
    [chatSessionsList],
  );

  const handleOpenChat = (
    e: React.MouseEvent<HTMLButtonElement>,
    openId: string,
  ) => {
    e.stopPropagation();
    const sameChat = sessionId === openId;

    if (!sameChat) {
      setSessionId(openId);
    }
    toggleOpenAction('gatheringChat', sameChat);
  };
  const handleRemoveChat = (
    e: React.MouseEvent<HTMLDivElement>,
    removeId: string,
  ) => {
    e.stopPropagation();
    closeChatSession(removeId);
  };

  if (!chatList.length) return null;

  return (
    <StyledListContainer className={cn({ showBorder })}>
      {chatList.map((session) => (
        <SessionChatItem
          key={session._id}
          session={session}
          isOpened={openAction === 'gatheringChat' && sessionId === session._id}
          handleOpenChat={(e) => handleOpenChat(e, session._id)}
          handleRemoveChat={(e) => handleRemoveChat(e, session._id)}
        />
      ))}
    </StyledListContainer>
  );
};

type ItemProps = {
  session: IChatSession;
  isOpened: boolean;
  handleOpenChat: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleRemoveChat: (e: React.MouseEvent<HTMLDivElement>) => void;
};

const SessionChatItem = ({
  session,
  isOpened,
  handleOpenChat,
  handleRemoveChat,
}: ItemProps) => {
  const gatheringName = useSelector((state) => state.chat.conversations.byId)[
    session.chatChannel
  ].name;

  return (
    <Stack position="relative" alignItems="center" justifyContent="center">
      <StyledTopLeftIcon className="icon" onClick={handleRemoveChat}>
        <Iconify icon="tabler:x" width={14} height={14} />
      </StyledTopLeftIcon>
      <ActionsBarButtonStyled
        variant="outlined"
        color={isOpened ? 'primary' : 'inherit'}
        onClick={handleOpenChat}
        tooltip={`#${gatheringName} - ${FormatTime.Date(session.startAt, 'PPPP - p')}`}
        placement="left"
      >
        <Iconify icon="tabler:message" />
      </ActionsBarButtonStyled>
    </Stack>
  );
};
